/**
 * OralIndicesCard — лаборатория за орални индекси.
 * DMFT, OHI-S, PlI, GI, CPI, BEWE и флуороза по Dean, с посока и връзки
 * към CAMBRA/EFP.
 * Данни: /api/dental-prevention/indices (+ DELETE /{id}) · /print
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Ruler, Plus, Trash2, Printer, Loader2, TrendingDown, TrendingUp, Link2,
} from "lucide-react";
import { useAuth } from "@/App";

const BAND_COLOR = {
  "отличен": "text-emerald-500", "добър": "text-emerald-500",
  "среден": "text-amber-500", "слаб": "text-rose-500",
  "много нисък": "text-emerald-500", "нисък": "text-emerald-500",
  "умерен": "text-amber-500", "висок": "text-rose-500",
  "много висок": "text-rose-600", "няма риск": "text-emerald-500",
  "нисък риск": "text-emerald-500", "среден риск": "text-amber-500",
  "висок риск": "text-rose-500",
};
const EMPTY = {
  date: new Date().toISOString().slice(0, 10), source: "self",
  dmft: { d: "", m: "", f: "" }, debris: {}, calculus: {}, pli: {}, gi: {},
  cpi: {}, bewe: {}, dean: "", note: "",
};

export default function OralIndicesCard({ isDark = false, cardCls = "", childId = null }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-prevention/indices`, {
        params: childId ? { child_id: childId } : {},
      });
      setD(data);
    } catch { setD(null); }
  }, [API, childId]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const inputCls = `px-2 py-1 rounded-lg border text-[12px] ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;
  const f = d.form;

  const setPath = (group, key, value) => setForm((p) => ({
    ...p, [group]: { ...(p[group] || {}), [key]: value === "" ? undefined : value },
  }));

  const save = async () => {
    setBusy(true);
    try {
      const num = (o) => Object.fromEntries(Object.entries(o || {})
        .filter(([, v]) => v !== undefined && v !== "")
        .map(([k, v]) => [k, Number(v)]));
      const { data } = await axios.post(`${API}/dental-prevention/indices`, {
        date: form.date, source: form.source, child_id: childId,
        dmft: num(form.dmft), debris: num(form.debris),
        calculus: num(form.calculus), pli: num(form.pli), gi: num(form.gi),
        cpi: Object.fromEntries(Object.entries(form.cpi || {})
          .filter(([, v]) => v !== undefined && v !== "")),
        bewe: num(form.bewe), dean: form.dean || null, note: form.note,
      });
      setD(data); setForm(null); toast.success("Индексите са изчислени");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не се записа");
    } finally { setBusy(false); }
  };

  const remove = async (rid) => {
    try {
      const { data } = await axios.delete(`${API}/dental-prevention/indices/${rid}`,
        { params: childId ? { child_id: childId } : {} });
      setD(data); toast.success("Изтрито");
    } catch { toast.error("Неуспешно"); }
  };

  const openPrint = async () => {
    try {
      const res = await axios.get(`${API}/dental-prevention/print`,
        { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Разпечатката не се отвори"); }
  };

  const Score = ({ group, criteria, label }) => (
    <div>
      <div className="text-[10px] font-bold uppercase opacity-60 mb-1">{label}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {f.teeth.map((t) => (
          <select key={t.key} value={(form[group] || {})[t.key] ?? ""}
            onChange={(e) => setPath(group, t.key, e.target.value)}
            className={`${inputCls} w-full`}
            data-testid={`indices-${group}-${t.key}`}>
            <option value="">{t.key.replace("t", "")} — n/a</option>
            {criteria.map((c, i) => <option key={i} value={i}>{t.key.replace("t", "")} · {i}</option>)}
          </select>
        ))}
      </div>
      <div className="text-[9px] opacity-55 mt-1">{criteria.join(" · ")}</div>
    </div>
  );

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-indices-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Ruler className="w-4 h-4 text-cyan-500" /> Лаборатория за орални индекси
          </div>
          <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button onClick={openPrint}
            className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1 bg-cyan-500/15 text-cyan-600"
            data-testid="indices-print">
            <Printer className="w-3.5 h-3.5" /> За зъболекаря
          </button>
          <button onClick={() => setForm({ ...EMPTY })}
            className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 inline-flex items-center gap-1"
            data-testid="indices-add">
            <Plus className="w-3.5 h-3.5" /> Нов запис
          </button>
        </div>
      </div>

      {/* последни стойности */}
      {d.latest && (
        <div className="mt-3" data-testid="indices-latest">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1.5">
            {d.latest.date} · {d.latest.source_bg}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {d.latest.indices.map((i) => (
              <div key={i.index} className={`p-2.5 rounded-xl border ${border}`}
                data-testid={`indices-value-${i.index}`}>
                <div className="text-[10px] opacity-70 leading-tight">{i.name_bg}</div>
                <div className="flex items-baseline gap-1.5 mt-0.5">
                  <span className="text-xl font-extrabold">{i.value}</span>
                  <span className={`text-[10px] font-bold ${BAND_COLOR[i.band_bg] || "opacity-70"}`}>
                    {i.band_bg}
                  </span>
                </div>
                {i.index === "DMFT" && (
                  <div className="text-[10px] opacity-70 mt-0.5">
                    D {i.d} · M {i.m} · F {i.f}
                    {i.care_index !== null && ` · грижа ${i.care_index}%`}
                  </div>
                )}
                {i.treatment_need_bg && (
                  <div className="text-[10px] opacity-70 mt-0.5">{i.treatment_need}: {i.treatment_need_bg}</div>
                )}
                {i.management_bg && (
                  <div className="text-[10px] opacity-70 mt-0.5">{i.management_bg}</div>
                )}
                <div className="text-[9px] opacity-45 mt-1">{i.method_bg}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* тренд */}
      {d.deltas?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="indices-deltas">
          {d.deltas.map((x) => (
            <span key={x.index}
              className={`text-[10px] font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                x.better ? "bg-emerald-500/15 text-emerald-600" : "bg-rose-500/15 text-rose-600"}`}
              data-testid={`indices-delta-${x.index}`}>
              {x.better ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
              {x.text_bg}
            </span>
          ))}
        </div>
      )}

      {/* връзки с другите модули */}
      {d.cross_links?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="indices-cross-links">
          {d.cross_links.map((c, i) => (
            <div key={i} className={`p-2 rounded-lg border ${border}`}>
              <div className="text-[11px] font-bold inline-flex items-center gap-1">
                <Link2 className="w-3.5 h-3.5 text-cyan-500" /> {c.title_bg}
              </div>
              <div className="text-[10px] opacity-75 mt-0.5">{c.text_bg}</div>
            </div>
          ))}
        </div>
      )}

      {/* формуляр */}
      {form && (
        <div className={`mt-3 p-3 rounded-xl border ${border} space-y-3`} data-testid="indices-form">
          <div className="flex flex-wrap gap-2 items-center">
            <input type="date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className={inputCls} data-testid="indices-date" />
            {["self", "dentist"].map((s) => (
              <button key={s} onClick={() => setForm({ ...form, source: s })}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                  form.source === s ? "border-cyan-500 bg-cyan-500/15 text-cyan-500" : `${border} opacity-70`}`}
                data-testid={`indices-source-${s}`}>
                {s === "self" ? "самооценка" : "от зъболекар"}
              </button>
            ))}
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">
              DMFT — постоянни зъби (без мъдреци)
            </div>
            <div className="flex gap-2">
              {[["d", "Кариозни"], ["m", "Извадени от кариес"], ["f", "Пломбирани"]].map(([k, lbl]) => (
                <input key={k} type="number" min="0" max="28" placeholder={lbl}
                  value={form.dmft[k]}
                  onChange={(e) => setForm({ ...form, dmft: { ...form.dmft, [k]: e.target.value } })}
                  className={`${inputCls} w-28`} data-testid={`indices-dmft-${k}`} />
              ))}
            </div>
          </div>

          <Score group="debris" criteria={f.debris_criteria_bg} label="DI-S — меки налепи (6 зъба, 0-3)" />
          <Score group="calculus" criteria={f.calculus_criteria_bg} label="CI-S — зъбен камък (6 зъба, 0-3)" />
          <Score group="gi" criteria={f.gi_criteria_bg} label="GI — гингивален индекс (първо той!)" />
          <Score group="pli" criteria={f.pli_criteria_bg} label="PlI — плаков индекс" />

          <div>
            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">CPI — код по секстанти</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {f.sextants.map((s) => (
                <select key={s.key} value={(form.cpi || {})[s.key] ?? ""}
                  onChange={(e) => setPath("cpi", s.key, e.target.value)}
                  className={`${inputCls} w-full`} data-testid={`indices-cpi-${s.key}`}>
                  <option value="">{s.label_bg} — n/a</option>
                  {f.cpi_codes.map((c) => <option key={c.code} value={c.code}>{c.label_bg}</option>)}
                </select>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">BEWE — ерозия по секстанти</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {f.sextants.map((s) => (
                <select key={s.key} value={(form.bewe || {})[s.key] ?? ""}
                  onChange={(e) => setPath("bewe", s.key, e.target.value)}
                  className={`${inputCls} w-full`} data-testid={`indices-bewe-${s.key}`}>
                  <option value="">{s.label_bg} — n/a</option>
                  {f.bewe_criteria_bg.map((c, i) => <option key={i} value={i}>{c}</option>)}
                </select>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <select value={form.dean} onChange={(e) => setForm({ ...form, dean: e.target.value })}
              className={inputCls} data-testid="indices-dean">
              <option value="">Флуороза по Dean — не е оценена</option>
              {f.dean_scale.map((s) => (
                <option key={s.code} value={s.code}>{s.label_bg}</option>
              ))}
            </select>
            <input placeholder="Забележка" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className={`${inputCls} flex-1 min-w-[140px]`} data-testid="indices-note" />
          </div>

          <div className="flex gap-2">
            <button onClick={save} disabled={busy}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="indices-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Изчисли и запази
            </button>
            <button onClick={() => setForm(null)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border ${border}`}
              data-testid="indices-cancel">Отказ</button>
          </div>
        </div>
      )}

      {/* история */}
      {d.records?.length > 1 && (
        <div className="mt-3 space-y-1" data-testid="indices-history">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">История</div>
          {d.records.map((r) => (
            <div key={r.record_id} className={`p-2 rounded-lg border ${border} flex items-center justify-between gap-2`}
              data-testid={`indices-record-${r.record_id}`}>
              <div className="min-w-0">
                <div className="text-[11px] font-bold">{r.date} · {r.source_bg}</div>
                <div className="text-[10px] opacity-70 truncate">
                  {r.indices.map((i) => `${i.index} ${i.value}`).join(" · ") || "няма стойности"}
                </div>
              </div>
              <button onClick={() => remove(r.record_id)} className="opacity-60 hover:opacity-100"
                data-testid={`indices-delete-${r.record_id}`}>
                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
